import React, { useState, useContext, useEffect } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import AuthenticationService from "../../../services/AuthenticationService";
import { Redirect } from "react-router";
import Input from "../../Input";
import Button from "../../Button";
import Alert from "../../Alert";
import Modal from "../../Modal";
import apiRequest from "../../../services/apiRequest";
import getRoutePath from "../../../routes/getRoutePath";
import CountryContext from "../../../CountryContext";
import { Link } from "react-router-dom";
import { BrandingBg } from "../../../components/Brandingbg";
import { PublicPage } from "@components/Public";
import Checkbox from "@ui-components/Checkbox";
import moment from "moment";

export default function Login(props) {
  const intl = useIntl();
  const { country } = useContext(CountryContext);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [openForgot, setOpenForgot] = useState(false);
  const [error, setError] = useState("");
  const [errorForgot, setErrorForgot] = useState("");
  const [email, setEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [oauth2Url, setOauth2Url] = useState("");

  const setOnError = (msg) => setError(msg);
  const setOffError = () => setError("");
  const setOnErrorForgot = (msg) => setErrorForgot(msg);
  const setOffErrorForgot = () => setErrorForgot("");

  const [toPath, ] = useState(
    props.location.state &&
    props.location.state.toPath &&
    props.location.state.toPath !== '/' ? props.location.state.toPath : "/home"
  )

  useEffect(() => {
    if (!oauth2Url) {
      AuthenticationService.getOauth2Url()
        .then((url) => {
          setOauth2Url(url);
        })
        .catch((err) => {
          console.log(err);
          setOnError("Cannot get OAuth2 url.");
        });
    }

    if (window.location.href.includes("unauthorized_user")) {
      setOnError(intl.formatMessage({ id: "access_denied" }));
    }
  }, [oauth2Url, intl]);

  function toggleForgot() {
    setEmailSent(false);
    setOpenForgot(!openForgot);
  }

  async function recoverPassword(email) {
    try {
      await apiRequest.post(
        "/users/password/forgot",
        {
          email: email,
          country: country.code,
        },
        { publicRequest: true }
      );
      setOffErrorForgot();
      setEmailSent(true);
    } catch (error) {
      setOnErrorForgot(intl.formatMessage({ id: "unregistered_email" }));
    }
  }

  const handleRecoverPassword = async (e) => {
    e.preventDefault();
    setLoading(true);

    await recoverPassword(email);
    setLoading(false);
  };

  const handleLogin = async (e) => {
    try {
      e.preventDefault();
      if (!username || !password) {
        return;
      }
      setLoading(true);
      setOffError();
      await AuthenticationService.login(
        username.trim(),
        password,
        country.code,
        toPath
      );
    } catch (error) {
      const errorDetail = error.response.data.detail;
      let message;
      if (errorDetail === "password_expired") {
        message = intl.formatMessage({
          id: "password_expired",
          defaultMessage: `La tua password è scaduta. Per favore, reimpostala utilizzando la funzionalità {f} presente sopra`,
        }, {
          f: <span className="italic font-normal">
            <FormattedMessage id="forgot_password" />
          </span>,
        });
      }
      else if (errorDetail === "account_locked") {
        message = intl.formatMessage({
          id: "account_locked",
          defaultMessage: "Il tuo account risulta inattivo da più di {num_days} giorni. Potrai accedere alla piattaforma solo dopo avere reimpostato la password. Per favore, utilizza la funzionalità {f} presente sopra",
        }, {
          num_days: 180,
          f: <span className="italic font-normal">
            <FormattedMessage id="forgot_password" />
          </span>,
        });
      }
      else if (errorDetail?.startsWith("user_locked")) {
        const match = errorDetail.match(/user_locked\((\d*)?,?([\d\-T:.Z]*)?\)/);
        if (match) {
          const numRemainingAttempts = match[1] ? parseInt(match[1], 10) : null;
          const tmsLockedUntil = match[2] || null;
          if (!!tmsLockedUntil) {
            const parsed = moment(tmsLockedUntil);
            message = intl.formatMessage({
              id: "user_locked_until",
              defaultMessage: "Hai superato il numero massimo di tentativi di accesso. Riprova dopo le {lb}",
            }, {lb: parsed.format("HH:mm")});
          } else if (!!numRemainingAttempts) {
            message = intl.formatMessage({
              id: "user_locked_remaining_attempts",
              defaultMessage: "Credenziali non corrette. Hai ancora {lb} tentativi",
            }, {lb: numRemainingAttempts});
          }
        }
      }
      else {
        message = errorDetail || intl.formatMessage({ id: errorDetail });
        console.error(error);
      }
      setOnError(message || intl.formatMessage({id: "wrong_email_or_password"}));
      setPassword("");
      setLoading(false);
    }
  };

  if (AuthenticationService.isUserAuthenticated()) {
    return <Redirect to={getRoutePath(toPath)} />;
  }

  return (
    <>
      <PublicPage>
        <div className="flex flex-row flex-1">
          <div className="flex-1 bg-white h-auto flex items-center justify-center">
            <form
              action="#"
              method="POST"
              onSubmit={handleLogin}
              className="w-full px-4 sm:px-8 py-8 sm:w-2/3 md:w-full lg:w-4/5 xl:w-2/3 2xl:w-1/2"
            >
              <h2 className="text-3xl mb-6 text-center font-semibold text-am-700">
                <FormattedMessage
                  id="geniuslab_copy_text"
                  defaultMessage="La piattaforma digitale per i tuoi progetti"
                />
              </h2>
              <div className="my-2 flex flex-col gap-1">
                <Input
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value);
                  }}
                  type="email"
                  required
                  errorMessage={intl.formatMessage({ id: "enter_email" })}
                  placeholder="Email"
                  showErrorDialog={false}
                />
                <Input
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                  }}
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  required
                />
              </div>
              <div className="flex items-center">
                <Checkbox
                  id="remember-me"
                  name="remember-me"
                  onChange={(e) => {
                    setShowPassword(e.target.checked);
                  }}
                />
                <label
                  htmlFor="remember-me"
                  className="ml-2 block text-sm text-black-500"
                >
                  <FormattedMessage id="show_password" />
                </label>
              </div>
              <div className="text-sm my-3">
                <p
                  onClick={toggleForgot}
                  className="font-semibold text-black-500 hover:text-am-500 cursor-pointer"
                >
                  <FormattedMessage id="forgot_password" />
                </p>
              </div>
              <div className="mt-7 flex flex-col gap-2">
                <Button
                  full
                  type="submit"
                  submitting={loading}
                  uppercase={false}
                >
                  Login
                </Button>
                {error && <Alert slim type="error" title={error} />}
                <Button
                  full
                  styleType="empty"
                  onClick={(e) => {
                    e.preventDefault();
                    window.location.href = oauth2Url;
                  }}
                  uppercase={false}
                >
                  <FormattedMessage
                    id="login_with_kerakoll_sso"
                    defaultMessage="Accedi con Kerakoll SSO"
                  />
                </Button>
              </div>
              {country.code !== "es" && (
                <div className="flex flex-row mt-6">
                  <FormattedMessage id="new_user" />
                  <Link to={`/${country.code}/signup`}>
                    <p className="ml-4 font-semibold text-am-700">
                      <FormattedMessage id="signup" />
                    </p>
                  </Link>
                </div>
              )}
            </form>
          </div>
          <div className="flex-1 h-auto hidden md:block">
            <BrandingBg />
          </div>
        </div>
      </PublicPage>
      {openForgot && (
        <Modal onExit={() => toggleForgot()}>
          <div className="w-full flex-col flex items-center my-6">
            <h3 className=" text-center text-2xl font-extrabold text-black-500">
              <FormattedMessage id="restore_password" />
            </h3>
            <form
              action="#"
              method="POST"
              className="w-full max-w-sm"
              onSubmit={handleRecoverPassword}
            >
              <div className="my-4">
                <Input
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                  }}
                  label="Email"
                  type="email"
                  required
                />
              </div>
              <div>
                <Button full type="submit" submitting={loading}>
                  <FormattedMessage id="send_link" />
                </Button>
              </div>
              {errorForgot && (
                <div className="mt-4">
                  <Alert slim type="error" title={errorForgot} />
                </div>
              )}
              {emailSent && (
                <div className="mt-4">
                  <Alert
                    slim
                    type="success"
                    title={`Email inviata con successo a ${email}`}
                  />
                </div>
              )}
            </form>
          </div>
        </Modal>
      )}
    </>
  );
}
